import { memo } from 'react';
import { BaseEdge, EdgeLabelRenderer, getBezierPath, type EdgeProps } from '@xyflow/react';
import type { EditorEdge } from '../../domain/editor-graph';
import { edgeColorFor, prefersReducedMotion, strokeDasharrayFor } from './edge-visuals';

/** SPEC-006 EDGE-REQ-017/EDGE-REQ-018 — a plain flow edge with an optional label. */
export const StandardEdge = memo(function StandardEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  selected,
  markerEnd,
  data,
}: EdgeProps<EditorEdge>) {
  const [path, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });
  const style = (data?.style as string) === 'dashed' ? 'dashed' : 'solid';
  const label = typeof data?.label === 'string' ? data.label : undefined;
  const animated = Boolean(data?.animated) && !prefersReducedMotion();
  const color = edgeColorFor(data?.colorOverride as string | undefined, 'var(--df-text-brand)');

  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        markerEnd={markerEnd}
        style={{
          stroke: selected ? 'var(--df-text-brand)' : color,
          strokeWidth: selected ? 2.5 : 1.75,
          strokeDasharray: strokeDasharrayFor(style),
        }}
        className={animated ? 'react-flow__edge-path-animated' : undefined}
      />
      {label ? (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            }}
            className="pointer-events-none rounded bg-[#111412] px-1.5 py-0.5 text-[10px] text-text-secondary"
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
});
